import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findMarriageCountry, MARRIAGE_ABROAD } from "@/data/marriage-abroad";
import CountryDeepContent from "@/components/CountryDeepContent";
import { normalizeCountrySlug } from "@/lib/country-hague";

export const Route = createFileRoute("/marriage/abroad/$country")({
  loader: ({ params }) => {
    const c = findMarriageCountry(params.country);
    if (!c) throw notFound();
    return { c };
  },
  head: ({ params }) => {
    const c = findMarriageCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Not found", "/marriage/abroad/$country", "th")}, { name: "robots", content: "noindex" }] };
    const path = `/marriage/abroad/${c.slug}`;
    const TITLE = `จดทะเบียนสมรสที่${c.th} (${c.en}) สำหรับคนไทย — คู่มือ 2026 | NYC`;
    const DESC = `คู่มือครบวงจร: คนไทยจดทะเบียนสมรสที่${c.th} — ${c.hague ? "Apostille ได้": "ต้อง Embassy Legalization"} · แปลเป็น${c.translationLang} · Waiting period ${c.waitingPeriodDays} วัน · ${c.sameSexRecognized ? "รองรับสมรสเพศเดียวกัน": "รองรับเฉพาะเพศต่างกัน"} · ${c.notes} — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/marriage/abroad/$country", "th")}, { name: "description", content: ctaDesc(DESC, "/marriage/abroad/$country", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/marriage/abroad/$country", "th")}, { property: "og:description", content: ctaDesc(DESC, "/marriage/abroad/$country", "th")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/en${path}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Marriage Abroad — ${c.en}`, priceFromThb: 8500,
        steps: [
          { name: "ประเมินคุณสมบัติ", text: `ตรวจ ${c.affirmationRequired ? "ต้องขอ Affirmation of Freedom to Marry จาก MFA" : "ไม่ต้องขอ Affirmation"} · Waiting period ${c.waitingPeriodDays} วัน` },
          { name: "รวบรวมเอกสารไทย", text: "สูติบัตร ทะเบียนบ้าน บัตร ปชช. ใบสำคัญโสด (คร.22) ใบเปลี่ยนชื่อ (ถ้ามี)" },
          { name: "แปล + MFA + Apostille", text: `แปลเป็น${c.translationLang} + MFA + ${c.hague ? "Apostille (Hague 15 ธ.ค. 2025)" : "Embassy Legalization"}` },
          { name: "ยื่น Notice / Registry", text: `ยื่นที่ ${c.registryTh} — waiting period ${c.waitingPeriodDays} วันตามกฎหมายท้องถิ่น` },
          { name: "จดทะเบียนและรับ Certificate", text: "รับ Marriage Certificate + บันทึกที่สถานทูตไทยเพื่อ recognition ในไทย" },
        ],
        faqs: [
          { q: `คนไทยจดทะเบียนสมรสที่${c.th} ต้องเตรียมอะไร?`, a: `เอกสารไทยหลัก + แปลเป็น${c.translationLang} + ${c.hague ? "Apostille" : "Embassy Legalization"} · Waiting period ${c.waitingPeriodDays} วัน` },
          { q: "Affirmation of Freedom to Marry คืออะไร?", a: c.affirmationRequired ? "หนังสือรับรองสถานะโสดที่ MFA ไทยออกให้ ใช้ยืนยันว่าไม่มีคู่สมรสอยู่ในไทย — ประเทศปลายทางบังคับ" : "ประเทศนี้ไม่บังคับ" },
          { q: "แปลเป็นภาษาอะไร?", a: c.translationLang },
          { q: "รองรับสมรสเพศเดียวกันไหม?", a: c.sameSexRecognized ? "รองรับ" : "ยังไม่รองรับ ณ ปัจจุบัน" },
          { q: "หลังจดที่ต่างประเทศแล้ว ต้องบันทึกในไทยไหม?", a: "ควรบันทึก — ยื่นทะเบียนสมรสต่างประเทศที่สถานทูตไทย หรืออำเภอในไทย เพื่อให้มีผลทางกฎหมายไทย (ใช้ยื่นภาษี วีซ่า คู่สมรส ฯลฯ)" },
          { q: "ค่าใช้จ่ายทั้งหมดที่ NYC?", a: "แพ็กเกจเอกสาร+แปล+MFA+Apostille เริ่ม ฿8,500–15,000 (ไม่รวมค่าธรรมเนียมของ registry ประเทศปลายทาง)" },
          { q: "ใช้เวลารวมนานแค่ไหน?", a: `เตรียมเอกสารในไทย 5–10 วัน + waiting period ${c.waitingPeriodDays} วันที่${c.th} + วันจดทะเบียน` },
          { q: "หมายเหตุพิเศษ?", a: c.notes },
        ],
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "Marriage", path: "/marriage" },
          { name: `Marriage Abroad — ${c.th}`, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">ไม่พบประเทศ</h1>      <QuotableStatsStrip cluster="marriage" locale="th" variant="light" />
    </main>
  ),
});

function Page() {
  const { c } = Route.useLoaderData();
  return (
    <>
    <ServiceLanding
      badge={`Marriage Abroad · ${c.en}`}
      title={`คนไทยจดทะเบียนสมรสที่${c.th}`}
      subtitle={c.notes}
      quickAnswer={{
        q: `คนไทยจดทะเบียนสมรสที่${c.th} ต้องเตรียมอะไร?`,
        a: `เอกสารไทยหลัก (สูติบัตร ทะเบียนบ้าน ใบสำคัญโสด) แปลเป็น${c.translationLang} + ${c.hague ? "Apostille (Hague)" : "Embassy Legalization"} + ${c.affirmationRequired ? "Affirmation of Freedom to Marry จาก MFA" : "ไม่ต้อง Affirmation"} · Waiting period ${c.waitingPeriodDays} วัน · ${c.sameSexRecognized ? "รองรับสมรสเพศเดียวกัน" : "เฉพาะเพศต่างกัน"}`,
      }}
      heroBullets={[
        `${c.hague ? "✓ Apostille (Hague)" : "✗ Embassy Legalization"} — ${c.hague ? "ประหยัดเวลา" : "ใช้เวลาเพิ่ม"}`,
        `แปลเป็น ${c.translationLang}`,
        `Waiting period ${c.waitingPeriodDays} วัน`,
        `${c.sameSexRecognized ? "รองรับสมรสเพศเดียวกัน" : "รองรับเฉพาะเพศต่างกัน"}`,
      ]}
      whoFor={{
        th: [`คนไทยที่จะจดทะเบียนสมรสที่${c.th}`, "คู่รักที่พำนักในต่างประเทศ", "คนไทยที่แต่งงานกับพลเมือง" + c.th],
        foreign: [`Foreign spouses of Thai nationals marrying in ${c.en}`],
      }}
      steps={[
        { name: "ตรวจคุณสมบัติ", text: `Waiting period ${c.waitingPeriodDays} วัน · ${c.affirmationRequired ? "ต้อง Affirmation" : "ไม่ต้อง Affirmation"}` },
        { name: "เตรียมเอกสารไทย", text: "สูติบัตร ทะเบียนบ้าน บัตร ปชช. ใบสำคัญโสด" },
        { name: "แปล+MFA+รับรอง", text: `แปลเป็น${c.translationLang} + MFA + ${c.hague ? "Apostille" : "Embassy"}` },
        { name: `ยื่นที่ ${c.registryTh}`, text: `Notice/Application ตามกฎหมาย${c.th}` },
        { name: "จดทะเบียน + บันทึกในไทย", text: "รับ Marriage Certificate + บันทึกที่สถานทูตไทย" },
      ]}
      pricing={[
        { label: "แพ็กเกจเอกสารครบชุด", price: "8,500–15,000", note: "แปล + MFA + Apostille" },
        { label: "Affirmation of Freedom to Marry", price: "800", note: "MFA fee + processing" },
        { label: "แปล sworn ท้องถิ่น (ถ้าจำเป็น)", price: "2,400–2,800/หน้า", note: `${c.translationLang}` },
        { label: "บันทึกทะเบียนสมรสในไทย", price: "1,500", note: "หลังกลับไทย" },
      ]}
      faqs={[
        { q: "Waiting period คืออะไร?", a: `ระยะเวลาที่กฎหมาย${c.th}บังคับให้ประกาศแจ้งความประสงค์ก่อนจดทะเบียน — ${c.waitingPeriodDays} วัน` },
        { q: "Apostille vs Embassy Legalization?", a: c.hague ? `${c.th}เป็นสมาชิก Hague — ใช้ Apostille ได้ ประหยัดเวลาและค่าใช้จ่ายกว่า Embassy Legalization` : `${c.th}ไม่ใช่สมาชิก Hague — ต้อง Embassy Legalization ที่สถานทูต${c.th}ในไทย` },
        { q: "รองรับสมรสเพศเดียวกันไหม?", a: c.sameSexRecognized ? "รองรับ — เอกสารและกระบวนการเหมือนคู่รักต่างเพศ" : `ณ ปัจจุบัน${c.th}ยังไม่รองรับ` },
        { q: "หมายเหตุพิเศษ?", a: c.notes },
        { q: "หลังจดต้องทำอะไรในไทย?", a: "ต้องนำทะเบียนสมรสต่างประเทศพร้อมคำแปลไทยที่ผ่านการรับรอง MFA ไปบันทึกที่สถานทูตไทยประเทศปลายทาง หรือกลับมาบันทึกที่ที่ว่าการอำเภอในไทย (คร.22) เพื่อให้การสมรสมีผลตามกฎหมายไทย มีผลต่อการใช้นามสกุล การจดทะเบียนบุตร มรดก และภาษี — NYC ดำเนินการครบวงจรทั้งแปล MFA และประสานอำเภอในราคาแพ็คเกจ" },
      ]}
      relatedLinks={[
        { to: "/marriage", label: "← Marriage hub" },
        ...MARRIAGE_ABROAD.filter((x) => x.slug !== c.slug).slice(0, 5).map((x) => ({
          to: `/marriage/abroad/${x.slug}` as const, label: `จดสมรสที่${x.th}`,
        })) as any,
        { to: "/apostille", label: "Apostille service" },
        { to: "/embassy", label: "Embassy Legalization" },
      ]}
    />
    <CountryDeepContent
      lang="th"
      service="marriage"
      countryTh={c.th}
      countryEn={c.en}
      hague={c.hague}
      priceFromTh={8500}
      turnaround={`${c.waitingPeriodDays} วัน + 5–10 วันทำการ`}
      slug={normalizeCountrySlug(c.slug)}
    />
    </>
  );
}
